/**
 * Request deduplication utility to prevent concurrent duplicate requests
 */

class RequestDeduplication {
  constructor() {
    // Map of active requests: key -> Promise
    this.activeRequests = new Map();
    
    // Map of request metadata for debugging
    this.requestMetadata = new Map();
    
    // Configuration
    this.config = {
      defaultTtl: 30000, // 30 seconds default TTL for deduplication
      maxConcurrentRequests: 10, // Maximum concurrent requests per key pattern
      cleanupInterval: 60000 // Cleanup interval in milliseconds
    };
    
    // Start periodic cleanup
    this.startCleanup();
  }

  /**
   * Generate a deduplication key for a request
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {object} params - Request parameters
   * @param {string} userId - User ID for user-specific deduplication
   * @returns {string} Deduplication key
   */
  generateKey(endpoint, method = 'GET', params = {}, userId = null) {
    // Create a stable key from the request parameters
    // Exclude the body from deduplication key generation to prevent FormData serialization
    const { body, ...paramsWithoutBody } = params;

    const paramString = Object.keys(paramsWithoutBody)
      .sort()
      .map(key => {
        const value = paramsWithoutBody[key];
        // Handle other non-serializable objects (body is already excluded)
        try {
          return `${key}=${JSON.stringify(value)}`;
        } catch (e) {
          return `${key}=[object]`;
        }
      })
      .join('&');

    const baseKey = `${method}:${endpoint}`;
    const fullKey = paramString ? `${baseKey}?${paramString}` : baseKey;

    // Include user ID for user-specific deduplication
    return userId ? `${userId}:${fullKey}` : fullKey;
  }

  /**
   * Check if a request is already in progress
   * @param {string} key - Deduplication key
   * @returns {boolean} True if request is in progress
   */
  isRequestInProgress(key) {
    return this.activeRequests.has(key);
  }

  /**
   * Get an existing request promise if it exists
   * @param {string} key - Deduplication key
   * @returns {Promise|null} Existing request promise or null
   */
  getExistingRequest(key) {
    return this.activeRequests.get(key) || null;
  }

  /**
   * Register a new request and return a deduplicated promise
   * @param {string} key - Deduplication key
   * @param {Function} requestFunction - Function that returns a promise for the actual request
   * @param {object} options - Deduplication options
   * @returns {Promise} Deduplicated request promise
   */
  async deduplicateRequest(key, requestFunction, options = {}) {
    const {
      ttl = this.config.defaultTtl,
      forceNew = false,
      metadata = {}
    } = options;

    // If force new is requested, remove existing request
    if (forceNew && this.activeRequests.has(key)) {
      this.removeRequest(key);
    }

    // Check if request is already in progress
    const existingRequest = this.activeRequests.get(key);
    if (existingRequest) {
      // Update metadata to track duplicate usage
      const metadata = this.requestMetadata.get(key);
      if (metadata) {
        metadata.duplicateCount = (metadata.duplicateCount || 0) + 1;
        metadata.lastDuplicateAt = Date.now();
      }
      
      return existingRequest;
    }

    // Create new request
    const startTime = Date.now();
    const requestMetadata = {
      key,
      startTime,
      ttl,
      duplicateCount: 0,
      metadata: {
        ...metadata,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        timestamp: new Date().toISOString()
      }
    };

    // Store metadata
    this.requestMetadata.set(key, requestMetadata);

    // Create the request promise
    const requestPromise = this.executeRequest(key, requestFunction, requestMetadata);
    
    // Store the active request
    this.activeRequests.set(key, requestPromise);

    // Set up automatic cleanup after TTL
    setTimeout(() => {
      this.removeRequest(key);
    }, ttl);

    return requestPromise;
  }

  /**
   * Execute the actual request with proper cleanup
   * @param {string} key - Deduplication key
   * @param {Function} requestFunction - Request function
   * @param {object} metadata - Request metadata
   * @returns {Promise} Request promise
   */
  async executeRequest(key, requestFunction, metadata) {
    try {
      const result = await requestFunction();
      
      // Update metadata with success info
      metadata.completedAt = Date.now();
      metadata.duration = metadata.completedAt - metadata.startTime;
      metadata.success = true;
      
      return result;
    } catch (error) {
      // Update metadata with error info
      metadata.completedAt = Date.now();
      metadata.duration = metadata.completedAt - metadata.startTime;
      metadata.success = false;
      metadata.error = {
        name: error.name,
        message: error.message,
        status: error.status
      };
      
      console.error(`[RequestDeduplication] Request failed: ${key} (${metadata.duration}ms)`, error);
      
      throw error;
    } finally {
      // Clean up the request after a short delay to allow for immediate duplicates
      setTimeout(() => {
        this.removeRequest(key);
      }, 1000);
    }
  }

  /**
   * Remove a request from active tracking
   * @param {string} key - Deduplication key
   */
  removeRequest(key) {
    const removed = this.activeRequests.delete(key);
    
    // Keep metadata for a bit longer for analysis
    setTimeout(() => {
      this.requestMetadata.delete(key);
    }, 5000);
  }

  /**
   * Cancel a specific request
   * @param {string} key - Deduplication key
   * @returns {boolean} True if request was cancelled
   */
  cancelRequest(key) {
    const request = this.activeRequests.get(key);
    if (request && typeof request.cancel === 'function') {
      request.cancel();
      this.removeRequest(key);
      return true;
    }
    return false;
  }

  /**
   * Cancel all active requests
   */
  cancelAllRequests() {
    const keys = Array.from(this.activeRequests.keys());
    let cancelledCount = 0;
    
    for (const key of keys) {
      if (this.cancelRequest(key)) {
        cancelledCount++;
      }
    }
    
    return cancelledCount;
  }

  /**
   * Get statistics about request deduplication
   * @returns {object} Deduplication statistics
   */
  getStatistics() {
    const activeCount = this.activeRequests.size;
    const metadataEntries = Array.from(this.requestMetadata.values());
    
    const completedRequests = metadataEntries.filter(m => m.completedAt);
    const totalDuplicates = metadataEntries.reduce((sum, m) => sum + (m.duplicateCount || 0), 0);
    const averageDuration = completedRequests.length > 0 
      ? completedRequests.reduce((sum, m) => sum + m.duration, 0) / completedRequests.length 
      : 0;
    
    const successfulRequests = completedRequests.filter(m => m.success);
    const failedRequests = completedRequests.filter(m => !m.success);
    
    return {
      activeRequests: activeCount,
      completedRequests: completedRequests.length,
      totalDuplicatesPrevented: totalDuplicates,
      averageRequestDuration: averageDuration,
      successRate: completedRequests.length > 0 
        ? (successfulRequests.length / completedRequests.length) * 100 
        : 0,
      failureRate: completedRequests.length > 0 
        ? (failedRequests.length / completedRequests.length) * 100 
        : 0,
      duplicatePreventionRate: (completedRequests.length + totalDuplicates) > 0 
        ? (totalDuplicates / (completedRequests.length + totalDuplicates)) * 100 
        : 0
    };
  }

  /**
   * Get detailed information about active and recent requests
   * @returns {object} Detailed request information
   */
  getDetailedInfo() {
    const activeRequests = Array.from(this.activeRequests.keys()).map(key => {
      const metadata = this.requestMetadata.get(key);
      return {
        key,
        startTime: metadata?.startTime,
        duration: metadata ? Date.now() - metadata.startTime : 0,
        duplicateCount: metadata?.duplicateCount || 0,
        metadata: metadata?.metadata
      };
    });

    const recentRequests = Array.from(this.requestMetadata.values())
      .filter(m => m.completedAt)
      .sort((a, b) => b.completedAt - a.completedAt)
      .slice(0, 20) // Last 20 completed requests
      .map(m => ({
        key: m.key,
        duration: m.duration,
        success: m.success,
        duplicateCount: m.duplicateCount,
        completedAt: m.completedAt,
        error: m.error
      }));

    return {
      activeRequests,
      recentRequests,
      statistics: this.getStatistics()
    };
  }

  /**
   * Start periodic cleanup of old metadata
   */
  startCleanup() {
    setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Clean up old metadata and stale requests
   */
  cleanup() {
    const now = Date.now();
    const maxAge = 5 * 60 * 1000; // 5 minutes
    
    let cleanedCount = 0;
    
    // Clean up old metadata
    for (const [key, metadata] of this.requestMetadata.entries()) {
      const age = now - metadata.startTime;
      if (age > maxAge && metadata.completedAt) {
        this.requestMetadata.delete(key);
        cleanedCount++;
      }
    }
    
    // Clean up stale active requests (shouldn't happen normally)
    for (const [key, request] of this.activeRequests.entries()) {
      const metadata = this.requestMetadata.get(key);
      if (metadata && (now - metadata.startTime) > metadata.ttl * 2) {
        console.warn(`[RequestDeduplication] Cleaning up stale request: ${key}`);
        this.activeRequests.delete(key);
        cleanedCount++;
      }
    }
    
    if (cleanedCount > 0) {
      // Cleaned up old entries
    }
  }

  /**
   * Create a wrapper function for API client methods
   * @param {Function} originalMethod - Original API client method
   * @param {string} methodName - Method name for logging
   * @returns {Function} Wrapped method with deduplication
   */
  wrapApiMethod(originalMethod, methodName) {
    return async (endpoint, options = {}, requestId = null) => {
      const {
        deduplication = true,
        deduplicationTtl,
        forceNew = false,
        userId,
        ...requestOptions
      } = options;

      // Skip deduplication if disabled
      if (!deduplication) {
        return originalMethod.call(this, endpoint, requestOptions, requestId);
      }

      // Generate deduplication key (exclude body to prevent FormData serialization)
      const keyOptions = { ...requestOptions };
      delete keyOptions.body; // Explicitly remove body to prevent any serialization

      const key = this.generateKey(
        endpoint,
        methodName.toUpperCase(),
        keyOptions,
        userId
      );

      // Create request function that preserves original options including FormData body
      const requestFunction = () => originalMethod.call(this, endpoint, options, requestId);

      // Execute with deduplication
      return this.deduplicateRequest(key, requestFunction, {
        ttl: deduplicationTtl,
        forceNew,
        metadata: {
          endpoint,
          method: methodName,
          requestId,
          userId
        }
      });
    };
  }
}

// Create singleton instance
const requestDeduplication = new RequestDeduplication();

export default requestDeduplication;
